import React, { useEffect, useRef, useState } from "react";
import { api, wsUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "@/lib/toast";
import { Send, Paperclip, X, FileText, Image as ImageIcon, Loader2, Lock, Eye } from "lucide-react";
import { getToken } from "@/lib/session";

const MAX_ATTACH_MB = 10;

/**
 * ProjectChatPanel — Per-project Client ↔ Admin chat.
 *
 * Props:
 *   projectId (string, required)
 *   title     (string, optional header title)
 *   subtitle  (string, optional secondary line)
 *   height    (string, tailwind height class — default `h-[70vh]`)
 */
export default function ProjectChatPanel({ projectId, title, subtitle, height = "h-[70vh]" }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState([]); // staged attachments (uploaded, not yet posted)
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [internalMode, setInternalMode] = useState(false);
  const wsRef = useRef(null);
  const endRef = useRef(null);
  const fileInputRef = useRef(null);
  const token = typeof window !== "undefined" ? getToken() : null;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/projects/${projectId}/messages`);
        if (!cancelled) setMessages(r.data);
        // Mark this thread read for me.
        api.post(`/projects/${projectId}/messages/read`).catch(() => {});
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Failed to load chat");
      }
    })();

    const roomId = `proj_${projectId}`;
    const url = wsUrl(`/api/ws/chat/${roomId}?token=${encodeURIComponent(token || "")}`);
    let ws;
    try {
      ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "message" && data.message?.project_id === projectId) {
            setMessages((prev) =>
              prev.some((m) => m.message_id === data.message.message_id) ? prev : [...prev, data.message]
            );
          }
        } catch (err) { /* ignore */ }
      };
      ws.onclose = () => {};
    } catch { /* ignore */ }

    return () => { cancelled = true; try { ws?.close(); } catch {} };
  }, [projectId, token]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, pending]);

  const stageFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (pending.length + files.length > 8) {
      toast.error("Max 8 attachments per message");
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > MAX_ATTACH_MB * 1024 * 1024) {
          toast.error(`${f.name}: too large (max ${MAX_ATTACH_MB} MB)`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(`/uploads/attachment`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPending((prev) => [...prev, r.data]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePending = (fid) => setPending((prev) => prev.filter((p) => p.file_id !== fid));

  const send = async () => {
    if (sending) return;
    const text = input.trim();
    if (!text && pending.length === 0) return;
    setSending(true);
    try {
      // Prefer HTTP so attachments always attach; WS still broadcasts the row afterwards on the server side.
      const r = await api.post(`/projects/${projectId}/messages`, {
        text,
        attachments: pending.map((p) => ({ file_id: p.file_id })),
        internal: isAdmin ? internalMode : false,
      });
      // Optimistically append (WS may also deliver — de-dup guard on message_id in ws handler)
      setMessages((prev) =>
        prev.some((m) => m.message_id === r.data.message_id) ? prev : [...prev, r.data]
      );
      setInput("");
      setPending([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const roleBadge = (role) => {
    if (role === "client") return "bg-slate-100 text-slate-600";
    return "bg-[#F3E8CE] text-[#0A2540]"; // gold for admin/super_admin
  };

  const authQuery = token ? `?auth=${encodeURIComponent(token)}` : "";

  return (
    <div className={`card-pb flex flex-col ${height}`} data-testid="project-chat-panel">
      <div className="px-5 py-4 border-b border-[#E2E8F0] dark:border-slate-700">
        <h3 className="font-heading text-lg text-[#0A2540] dark:text-white">{title || "Project messages"}</h3>
        <p className="text-xs text-[#475569] dark:text-slate-400">{subtitle || "Direct line to the PowerBuilt team"}</p>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-sm text-[#94A3B8] mt-10">
            <p className="mb-1">No messages yet.</p>
            <p className="text-xs">Say hello or share a photo of your site.</p>
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === user?.user_id;
          const isInternal = !!m.internal;
          const bubbleCls = isInternal
            ? "bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700/50 text-[#0A2540] dark:text-amber-100 rounded-lg"
            : mine
              ? "bg-[#0A2540] text-white rounded-br-sm"
              : "bg-slate-100 dark:bg-slate-800 text-[#0A2540] dark:text-white rounded-bl-sm";
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`pmsg-${m.message_id}`}>
              <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${bubbleCls}`}>
                {isInternal && (
                  <div className="flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300">
                    <Lock className="w-3 h-3" />
                    Internal · not visible to customer
                  </div>
                )}
                {!mine && !isInternal && (
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-xs font-medium text-[#C9A356]">{m.sender_name}</span>
                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${roleBadge(m.sender_role)}`}>
                      {m.sender_role === "super_admin" ? "admin" : m.sender_role}
                    </span>
                  </div>
                )}
                {isInternal && (
                  <div className="flex items-center gap-1.5 mb-0.5 text-xs">
                    <span className="font-medium text-[#0A2540] dark:text-amber-100">{m.sender_name}</span>
                    <span className="text-[10px] text-amber-700/80 dark:text-amber-300/80">
                      · {(m.sender_role || "").replace("super_admin", "admin")}
                    </span>
                  </div>
                )}
                {m.text && <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</div>}
                {(m.attachments || []).length > 0 && (
                  <div className={`mt-2 grid gap-1.5 ${(m.attachments || []).length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                    {m.attachments.map((a) => (
                      a.kind === "image" ? (
                        <a
                          key={a.file_id}
                          href={`${backendUrl}${a.url}${authQuery}`}
                          target="_blank"
                          rel="noreferrer"
                          className="block rounded-lg overflow-hidden border border-black/10"
                          data-testid={`pmsg-attach-${a.file_id}`}
                        >
                          <img
                            src={`${backendUrl}${a.url}${authQuery}`}
                            alt={a.name}
                            className="w-full h-32 object-cover"
                            loading="lazy"
                          />
                        </a>
                      ) : (
                        <a
                          key={a.file_id}
                          href={`${backendUrl}${a.url}${authQuery}`}
                          target="_blank"
                          rel="noreferrer"
                          className={`flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs ${mine ? "bg-white/10 hover:bg-white/20" : "bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-950 border border-[#E2E8F0] dark:border-slate-700"}`}
                          data-testid={`pmsg-attach-${a.file_id}`}
                        >
                          <FileText className="w-4 h-4 shrink-0" />
                          <span className="truncate flex-1">{a.name}</span>
                        </a>
                      )
                    ))}
                  </div>
                )}
                <div className={`text-[10px] mt-1 ${isInternal ? "text-amber-700/80 dark:text-amber-300/70" : mine ? "text-slate-300" : "text-[#94A3B8]"}`}>
                  {new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Staged attachments */}
      {pending.length > 0 && (
        <div className="border-t border-[#E2E8F0] dark:border-slate-700 px-3 py-2 flex flex-wrap gap-2 bg-slate-50/60 dark:bg-slate-900/40">
          {pending.map((a) => (
            <div key={a.file_id} className="relative flex items-center gap-1.5 pl-1 pr-6 py-1 bg-white dark:bg-slate-800 border border-[#E2E8F0] dark:border-slate-700 rounded-lg text-xs" data-testid={`pending-${a.file_id}`}>
              {a.kind === "image" ? (
                <img src={`${backendUrl}${a.url}${authQuery}`} alt="" className="w-8 h-8 object-cover rounded" />
              ) : (
                <div className="w-8 h-8 rounded bg-slate-100 dark:bg-slate-700 grid place-items-center">
                  <FileText className="w-4 h-4 text-[#475569]" />
                </div>
              )}
              <span className="truncate max-w-[130px]">{a.name}</span>
              <button
                onClick={() => removePending(a.file_id)}
                className="absolute -right-1 -top-1 w-4 h-4 grid place-items-center rounded-full bg-slate-700 text-white hover:bg-red-500"
                data-testid={`remove-pending-${a.file_id}`}
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Composer mode toggle (admins only) */}
      {isAdmin && (
        <div className="border-t border-[#E2E8F0] dark:border-slate-700 px-3 py-1.5 flex items-center gap-1 bg-slate-50/60 dark:bg-slate-900/40" data-testid="composer-mode-toggle">
          <button
            type="button"
            onClick={() => setInternalMode(false)}
            data-testid="composer-mode-customer"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] uppercase tracking-wider font-bold transition ${
              !internalMode
                ? "bg-emerald-600 text-white"
                : "text-[#475569] dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
          >
            <Eye className="w-3 h-3" /> Message to customer
          </button>
          <button
            type="button"
            onClick={() => setInternalMode(true)}
            data-testid="composer-mode-internal"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] uppercase tracking-wider font-bold transition ${
              internalMode
                ? "bg-amber-500 text-white"
                : "text-[#475569] dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
          >
            <Lock className="w-3 h-3" /> Internal note
          </button>
        </div>
      )}

      {/* Composer */}
      <div className={`border-t border-[#E2E8F0] dark:border-slate-700 p-3 flex items-end gap-2 ${
        internalMode ? "bg-amber-50 dark:bg-amber-900/20" : "bg-white dark:bg-slate-900"
      }`}>
        <input
          type="file"
          multiple
          className="hidden"
          ref={fileInputRef}
          onChange={(e) => stageFiles(e.target.files)}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
          data-testid="pchat-file-input"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          title="Attach files"
          data-testid="pchat-attach-btn"
          className="w-10 h-10 shrink-0 grid place-items-center rounded-md border border-[#E2E8F0] dark:border-slate-700 text-[#475569] hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
        </button>
        <textarea
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={
            internalMode
              ? "Internal note — only PowerBuilt staff see this. Enter to save · Shift+Enter for new line"
              : "Type a message… (Enter to send · Shift+Enter for new line)"
          }
          data-testid="pchat-input"
          className={`flex-1 resize-none border rounded-md px-4 py-2.5 outline-none focus:ring-2 max-h-[120px] ${
            internalMode
              ? "border-amber-300 dark:border-amber-700 bg-white/70 dark:bg-slate-900/70 focus:ring-amber-500/40 focus:border-amber-500"
              : "border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:ring-[#C9A356]/40 focus:border-[#C9A356]"
          }`}
        />
        <button
          onClick={send}
          disabled={sending || uploading || (!input.trim() && pending.length === 0)}
          className={`h-10 px-4 flex items-center gap-1.5 rounded-md font-medium transition disabled:opacity-50 ${
            internalMode
              ? "bg-amber-500 hover:bg-amber-600 text-white"
              : "btn-accent"
          }`}
          data-testid="pchat-send"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : (internalMode ? <Lock className="w-4 h-4" /> : <Send className="w-4 h-4" />)}
          <span className="text-sm hidden sm:inline">{internalMode ? "Save note" : "Send"}</span>
        </button>
      </div>
    </div>
  );
}
